/** Round avatar with initials; colour is stable per name. */
import clsx from 'clsx';
import { initialsOf } from '../lib/resources.js';
import styles from './ui.module.css';

const PALETTE = 10;

function hash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (Math.imul(h, 31) + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function Avatar({ name, size = 28, color, className, title }: { name: string; size?: number; color?: string | null; className?: string; title?: string }) {
  const bg = color ?? `var(--project-${(hash(name) % PALETTE) + 1})`;
  return (
    <span
      className={clsx(styles.avatar, className)}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)), background: bg }}
      title={title ?? name}
      aria-hidden="true"
    >
      {initialsOf(name)}
    </span>
  );
}

/** Up to `max` overlapping avatars plus a "+n" counter; the names are available to screen readers. */
export function AvatarStack({ names, max = 4, size = 24 }: { names: string[]; max?: number; size?: number }) {
  const shown = names.slice(0, max);
  const rest = names.length - shown.length;
  return (
    <span className={styles.avatarStack} aria-label={names.join(', ')} role="img">
      {shown.map((n) => (
        <Avatar key={n} name={n} size={size} />
      ))}
      {rest > 0 && (
        <span className={clsx(styles.avatar, styles.avatarMore)} style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }} aria-hidden="true">
          +{rest}
        </span>
      )}
    </span>
  );
}
